"use client";

import { PrefetchKind } from "next/dist/client/components/router-reducer/router-reducer-types";
import Link, { useLinkStatus } from "next/link";
import { useRouter } from "next/navigation";
import { type ComponentProps, useEffect, useState } from "react";
import { strings } from "@/content/strings";
import { cn } from "@/lib/utils";

function NavigationPending() {
  const { pending } = useLinkStatus();
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (!pending) {
      setVisible(false);
      return;
    }
    const timer = window.setTimeout(() => setVisible(true), 100);
    return () => window.clearTimeout(timer);
  }, [pending]);

  if (!pending || !visible) return null;
  return (
    <span className="pointer-events-none absolute inset-0 rounded-[inherit] ring-2 ring-primary/40 ring-inset motion-safe:animate-pulse">
      <output className="sr-only">{strings.ui.loading}</output>
    </span>
  );
}

// Next 16.2's default router.prefetch uses AUTO, which stops at loading.tsx.
// Keep the version-specific option here so guides warm their complete content.
export function prefetchFullRoute(
  router: ReturnType<typeof useRouter>,
  href: string,
) {
  router.prefetch(href, { kind: PrefetchKind.FULL });
}

/** Warm full dynamic routes on keyboard or pointer intent. */
export function IntentPrefetchLink({
  href,
  children,
  className,
  onFocus,
  onMouseEnter,
  ...props
}: Omit<ComponentProps<typeof Link>, "href"> & { href: string }) {
  const router = useRouter();

  return (
    <Link
      {...props}
      href={href}
      className={cn("interaction-focus relative", className)}
      onFocus={(event) => {
        onFocus?.(event);
        prefetchFullRoute(router, href);
      }}
      onMouseEnter={(event) => {
        onMouseEnter?.(event);
        prefetchFullRoute(router, href);
      }}
    >
      {children}
      <NavigationPending />
    </Link>
  );
}
